// EditableServicesPage.jsx
import { lazy, Suspense, useEffect, useState } from 'react';
import Footer from '../home/Footer';
import './Services.css';
import './ServicesArchitecturePipeline.css';
import '../../components/services/editable/editable.css';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import ServicesHeroScene from './ServicesHeroScene';
import { prefetchServicesHeroLottie } from './serviceLottieLoaders';
import { useEditablePage } from './editable/useEditablePage';

const CoreCapabilities = lazy(() => import('./CoreCapabilities'));

/**
 * Generic, CMS-driven /services listing page — the Services-page counterpart
 * to EditableAboutPage / TemplateServicePage. editable=false: plain public
 * render (byte-identical markup to the original hardcoded Services.jsx +
 * ServicesArchitecturePipeline.jsx). editable=true: inline WYSIWYG for the
 * hero copy and the AI-development pipeline steps, using the same shared
 * editing helpers (useEditablePage) as every other editable page.
 *
 * The "Core Capabilities" cards come from `servicePages` (one card per page
 * created in the "Service Pages" admin tab). When `fullContent`/
 * `onFullContentChange` are supplied (the admin editor route), each card's
 * title/description/features are also directly editable right here — see
 * CoreCapabilities.jsx — writing straight into that card's underlying
 * service page, same as editing it from its own "Service Pages" editor.
 *
 * The pipeline section is rebuilt inline here (same classNames/markup as the
 * standalone ServicesArchitecturePipeline component) so its text can be made
 * editable without touching that component.
 */
export default function EditableServicesPage({
  data,
  styles,
  editable = false,
  onChange,
  preview = false,
  fullContent,
  onFullContentChange,
}) {
  const isEditing = editable && !preview;

  const heroRef = useScrollAnimation({ threshold: 0.14, immediate: isEditing });
  const [activeService, setActiveService] = useState('ai-chatbots');
  const [activeStep, setActiveStep] = useState(data.pipeline.steps[0] ? 0 : -1);

  const { T, RemoveBtn, AddBtn, SectionToggle, showSection, isSectionHidden } = useEditablePage({
    data,
    styles,
    onChange,
    isEditing,
  });

  useEffect(() => {
    prefetchServicesHeroLottie();
  }, []);

  return (
    <div className="services-page">
      {showSection('hero') ? (
        <section
          className="services-hero-section"
          ref={heroRef}
          data-svcpg-hidden={isEditing && isSectionHidden('hero') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="hero" label="Hero" />
          <div className="services-hero__container">
            <div className="services-hero__split">
              <div className="services-hero__copy">
                <h1 className="services-hero__title">
                  {T('hero.titleAccent', 'hero.titleAccent', data.hero.titleAccent, 'span', 'services-hero__title-accent')}{' '}
                  {T('hero.titleRest', 'hero.titleRest', data.hero.titleRest, 'span')}
                </h1>
                {T('hero.lede', 'hero.lede', data.hero.lede, 'p', 'services-hero__lede')}
              </div>
              <ServicesHeroScene activeService={activeService} />
            </div>

            {showSection('pipeline') ? (
              <section
                className="services-pipeline"
                aria-labelledby="services-pipeline-title"
                data-svcpg-hidden={isEditing && isSectionHidden('pipeline') ? 'true' : undefined}
              >
                <SectionToggle sectionKey="pipeline" label="AI development pipeline" />
                <div className="services-pipeline__head">
                  <p className="services-pipeline__eyebrow">
                    {T('pipeline.eyebrow', 'pipeline.eyebrow', data.pipeline.eyebrow, 'span')}
                  </p>
                  <h2 id="services-pipeline-title" className="services-pipeline__title">
                    {T('pipeline.titleLine', 'pipeline.titleLine', data.pipeline.titleLine, 'span')}{' '}
                    {T('pipeline.titleAccent', 'pipeline.titleAccent', data.pipeline.titleAccent, 'span', 'services-pipeline__title-accent')}
                  </h2>
                </div>

                <div className="services-pipeline__track-wrap" aria-hidden="true">
                  <span className="services-pipeline__track" />
                  <span
                    className="services-pipeline__track-progress"
                    style={{ '--progress': `${(Math.max(0, activeStep) / Math.max(1, data.pipeline.steps.length - 1)) * 100}%` }}
                  />
                </div>

                <div className="services-pipeline__grid">
                  {data.pipeline.steps.map((step, index) => (
                    <div
                      key={index}
                      className={`services-pipeline__card${activeStep === index ? ' is-active' : ''}${isEditing ? ' svcpg-removable' : ''}`}
                      onMouseEnter={() => setActiveStep(index)}
                      onFocus={() => setActiveStep(index)}
                      onClick={() => setActiveStep(index)}
                    >
                      <span className="services-pipeline__index">{`0${index + 1}`}</span>
                      {T(`pipeline.steps.${index}.title`, `pipeline.steps.${index}.title`, step.title, 'h3', 'services-pipeline__card-title')}
                      {T(`pipeline.steps.${index}.description`, `pipeline.steps.${index}.description`, step.description, 'p', 'services-pipeline__card-description')}
                      <RemoveBtn path="pipeline.steps" index={index} label="Remove step" />
                    </div>
                  ))}
                </div>
                <AddBtn
                  path="pipeline.steps"
                  label="Add step"
                  template={{ title: 'New step', description: 'Step description' }}
                />
              </section>
            ) : null}
          </div>
        </section>
      ) : null}

      {showSection('coreCapabilities') ? (
        <div
          className="case-study-page-container"
          data-svcpg-hidden={isEditing && isSectionHidden('coreCapabilities') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="coreCapabilities" label="Core Capabilities" />
          <Suspense fallback={<div className="core-capabilities core-capabilities--loading" aria-hidden="true" />}>
            <CoreCapabilities
              onServiceHover={setActiveService}
              onServiceLeave={() => setActiveService('ai-chatbots')}
              isEditing={isEditing}
              fullContent={fullContent}
              onFullContentChange={onFullContentChange}
              titleNode={
                <h2 className="core-capabilities__title">
                  {T('coreCapabilities.title', 'coreCapabilities.title', data.coreCapabilities.title, 'span')}
                </h2>
              }
              subtitleNode={
                <p className="core-capabilities__subtitle">
                  {T('coreCapabilities.subtitle', 'coreCapabilities.subtitle', data.coreCapabilities.subtitle, 'span')}
                </p>
              }
            />
          </Suspense>
        </div>
      ) : null}

      <Footer />
    </div>
  );
}
